import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../redux/store";
import { getLastPunch } from "../../../redux/features/punch/PunchApi";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout/slice";

const WebPunchTile: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [currentTime, setCurrentTime] = useState(new Date());

    const { lastPunch, isLoadingLastPunch } = useSelector((state: RootState) => state.punch);

    // Update current time every second
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Load last punch data on component mount
    useEffect(() => {
        dispatch(getLastPunch());
    }, [dispatch]);

    const handlePunchClick = () => {
        dispatch(setTopNavNameTitle("Time Clock"));
        navigate("/punch");
    };

    // Determine current punch state based on last punch
    const getCurrentPunchState = () => {
        if (!lastPunch) {
            return { isClockedIn: false, isOnBreak: false };
        }
        if (lastPunch.reset_punch_state) {
            return { isClockedIn: false, isOnBreak: false };
        }
        switch (lastPunch.punch_status) {
            case 0: return { isClockedIn: true, isOnBreak: false };
            case 1: return { isClockedIn: false, isOnBreak: false };
            case 2: return { isClockedIn: true, isOnBreak: false };
            case 3: return { isClockedIn: true, isOnBreak: true };
            default: return { isClockedIn: false, isOnBreak: false };
        }
    };

    const { isClockedIn, isOnBreak } = getCurrentPunchState();

    const getStatusText = () => {
        if (isOnBreak) return "On Break";
        if (isClockedIn) return "Clocked In";
        return "Not Clocked In";
    };

    const getStatusColor = () => {
        if (isOnBreak) return "text-yellow-600 bg-yellow-50 border-yellow-200";
        if (isClockedIn) return "text-green-600 bg-green-50 border-green-200";
        return "text-gray-600 bg-gray-50 border-gray-200";
    };

    return (
        <div className="p-6 h-full cursor-pointer" onClick={handlePunchClick}>
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl text-blue-500 leading-none flex items-center">
                    Time Clock
                </h2>
                <Clock className="w-5 h-5 flex-shrink-0 text-blue-500" />
            </div>

            <div className="flex-1 flex flex-col items-center justify-center">
                {isLoadingLastPunch ? (
                    <div className="flex justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    </div>
                ) : (
                    <div className="text-center">
                        {/* Current Date */}
                        <div className="text-sm text-gray-500 mb-2">
                            {format(currentTime, 'EEEE, MMMM do, yyyy')}
                        </div>

                        {/* Current Time */}
                        <div className="font-mono text-2xl font-light text-gray-900 mb-3">
                            {format(currentTime, 'HH:mm:ss')}
                        </div>

                        {/* Status Badge */}
                        <div className={`inline-flex items-center px-3 py-1 rounded-full border text-sm font-medium mb-4 ${getStatusColor()}`}>
                            {getStatusText()}
                        </div>

                        {/* Last Punch Info */}
                        {lastPunch && (
                            <div className="text-xs text-gray-500">
                                Last: {format(new Date(lastPunch.datetime), 'MMM d, h:mm a')}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default WebPunchTile; 
